/**
 * Utilidades para manejo y formateo de RUT chileno
 * 
 * Formatos soportados:
 * - Con puntos: XX.XXX.XXX-X (para display/UI)
 * - Sin puntos: XXXXXXXX-X (para APIs)
 * - Limpio: XXXXXXXXX (sin separadores)
 */

export type RutFormat = 'dots' | 'no-dots' | 'clean';

/**
 * Limpia un RUT eliminando todos los separadores (puntos, guiones, espacios)
 * @param rut - RUT en cualquier formato
 * @returns RUT sin separadores (ej: "150770548")
 */
export function cleanRut(rut: string): string {
  if (!rut) return '';
  return rut.replace(/[.\-\s]/g, '').toLowerCase();
}

/**
 * Formatea un RUT con puntos (formato visual para UI)
 * @param rut - RUT en cualquier formato
 * @returns RUT formateado como XX.XXX.XXX-X (ej: "15.077.054-8")
 */
export function formatRutWithDots(rut: string): string {
  if (!rut) return '';
  
  const clean = cleanRut(rut);
  if (clean.length < 2) return clean;
  
  const dv = clean.slice(-1);
  const numero = clean.slice(0, -1);
  
  // Agregar puntos cada 3 dígitos desde la derecha
  const numeroFormateado = numero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${numeroFormateado}-${dv}`;
}

/**
 * Formatea un RUT sin puntos pero con guion (formato para APIs)
 * @param rut - RUT en cualquier formato
 * @returns RUT formateado como XXXXXXXX-X (ej: "15077054-8")
 */
export function formatRutWithoutDots(rut: string): string {
  if (!rut) return '';
  
  const clean = cleanRut(rut);
  if (clean.length < 2) return clean;
  
  const dv = clean.slice(-1);
  const numero = clean.slice(0, -1);
  
  return `${numero}-${dv}`;
}

/**
 * Normaliza un RUT a un formato específico
 * @param rut - RUT en cualquier formato
 * @param targetFormat - Formato deseado: 'dots', 'no-dots', o 'clean'
 * @returns RUT en el formato especificado
 */
export function normalizeRut(rut: string, targetFormat: RutFormat = 'no-dots'): string {
  switch (targetFormat) {
    case 'dots':
      return formatRutWithDots(rut);
    case 'no-dots':
      return formatRutWithoutDots(rut);
    case 'clean':
      return cleanRut(rut);
    default:
      return formatRutWithoutDots(rut);
  }
}

/**
 * Valida que un RUT tenga un formato válido
 * @param rut - RUT a validar
 * @param expectedFormat - Formato esperado (opcional)
 * @returns true si el formato es válido
 */
export function isValidRutFormat(rut: string, expectedFormat?: RutFormat): boolean {
  if (!rut) return false;
  
  if (expectedFormat === 'dots') {
    // Formato: XX.XXX.XXX-X o X.XXX.XXX-X
    return /^\d{1,2}(\.\d{3}){2}-[\dkK]$/.test(rut);
  } else if (expectedFormat === 'no-dots') {
    // Formato: XXXXXXXX-X
    return /^\d{7,8}-[\dkK]$/.test(rut);
  } else if (expectedFormat === 'clean') {
    // Formato: XXXXXXXXX (sin separadores)
    return /^\d{8,9}[\dkK]?$/.test(rut);
  }
  
  // Sin formato específico, aceptar cualquiera
  const clean = cleanRut(rut);
  return clean.length >= 2 && clean.length <= 9;
}

/**
 * Valida el dígito verificador de un RUT
 * @param rut - RUT en cualquier formato
 * @returns true si el dígito verificador es correcto
 */
export function isValidRut(rut: string): boolean {
  if (!rut) return false;
  
  const clean = cleanRut(rut);
  if (clean.length < 2) return false;
  
  const dv = clean.slice(-1);
  const numero = clean.slice(0, -1);
  
  // Algoritmo módulo 11
  let suma = 0;
  let multiplicador = 2;
  
  for (let i = numero.length - 1; i >= 0; i--) {
    suma += parseInt(numero[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  
  const resto = suma % 11;
  const dvCalculado = 11 - resto;
  
  let dvEsperado: string;
  if (dvCalculado === 11) {
    dvEsperado = '0';
  } else if (dvCalculado === 10) {
    dvEsperado = 'k';
  } else {
    dvEsperado = dvCalculado.toString();
  }
  
  return dv === dvEsperado;
}

/**
 * Extrae solo el número del RUT (sin dígito verificador)
 * @param rut - RUT en cualquier formato
 * @returns Número del RUT sin DV
 */
export function getRutNumber(rut: string): string {
  if (!rut) return '';
  const clean = cleanRut(rut);
  return clean.slice(0, -1);
}

/**
 * Extrae el dígito verificador del RUT
 * @param rut - RUT en cualquier formato
 * @returns Dígito verificador
 */
export function getRutDV(rut: string): string {
  if (!rut) return '';
  const clean = cleanRut(rut);
  return clean.slice(-1);
}
